import { memo, useState } from 'react';
import { IconCaret } from '@components/icons';
import { Transition } from '@headlessui/react';
import classNames from 'classnames';

import { NestedCheckboxOptionProps } from '../src/nested-checkbox-list.type';
import { isFullySelected, isPartiallySelected } from '../utils';

import { CheckboxItem } from './checkbox-item';

export const NestedCheckboxOption = memo(
  ({
    item,
    item: { children, id, disabled, name, description },
    isSelected,
    isExpanded = false,
    onSelect,
    checkboxOptionClassName = 'fill-primary',
    customStyles,
    depth,
  }: NestedCheckboxOptionProps) => {
    const [isLocalExpanded, setIsLocalExpanded] = useState(isExpanded);
    const itemIsPartiallySelected = isPartiallySelected(item, isSelected);
    const hasChildren = children.length > 0;
    const baseWidth = 32;
    const caretWidth = hasChildren ? 0 : 32;
    const label = `${name} ${hasChildren ? `(${children.length})` : ''}`;
    return (
      <div className='w-full'>
        <div
          className={classNames('flex flex-row items-center w-full rounded-sm', checkboxOptionClassName)}
          style={{
            paddingLeft: `${baseWidth * depth + caretWidth}px`,
          }}>
          {hasChildren && (
            <button
              type='button'
              data-testid={`expand-${id}`}
              className={classNames('flex items-center justify-between mr-2')}
              onClick={() => setIsLocalExpanded((prev) => !prev)}>
              <IconCaret data-testid='icon-caret' className={classNames('w-6 h-6', { 'rotate-90': isLocalExpanded })} />
            </button>
          )}
          <CheckboxItem
            id={`${id}-checkbox`}
            isDisabled={disabled}
            isSelected={isFullySelected(item, isSelected)}
            isIndeterminated={itemIsPartiallySelected}
            label={label}
            description={description}
            customStyles={customStyles}
            onChange={() => onSelect(id, hasChildren)}
          />
        </div>
        {
          <Transition
            as={'div'}
            appear={true}
            enter='transition duration-200 ease-in-out'
            enterFrom='-translate-y-10 opacity-0'
            enterTo='-translate-y-0 opacity-100'
            leave='transition duration-200 ease-in-out'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
            show={hasChildren && isLocalExpanded}>
            {item.children.map((child) => (
              <NestedCheckboxOption
                key={child.id}
                item={child}
                isExpanded={isLocalExpanded}
                checkboxOptionClassName={checkboxOptionClassName}
                customStyles={customStyles}
                isSelected={isSelected}
                onSelect={onSelect}
                depth={depth + 1}
              />
            ))}
          </Transition>
        }
      </div>
    );
  }
);
